import { useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { DESIGN_ASSETS_COLLECTION, hydrateDesignAsset } from '../services/designAssetService'
import { DEFAULT_DESIGN_CANVAS, type DesignAsset } from '../types/design'
import { isNoSignInPreviewActive } from '../debug'

const fromSnapshot = (snapshot: QueryDocumentSnapshot<DocumentData>): DesignAsset => {
  const hydrated = hydrateDesignAsset(snapshot.id, snapshot.data() as Record<string, unknown>)
  return hydrated ?? {
    id: snapshot.id,
    workspaceId: '',
    title: 'Untitled design',
    mode: 'FREE',
    canvas: DEFAULT_DESIGN_CANVAS,
    shapes: [],
    createdById: '',
    createdByName: '',
    createdAt: null,
    updatedAt: null,
  }
}

const DEBUG_DESIGN_PREFIX = 'timelining.debug.design.'
export const DEBUG_DESIGN_SAVED_EVENT = 'timelining:debug-design-saved'

const readDebugDesignAsset = (assetId: string): DesignAsset | null => {
  try {
    const raw = window.localStorage.getItem(`${DEBUG_DESIGN_PREFIX}${assetId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DesignAsset>
    return {
      id: typeof parsed.id === 'string' ? parsed.id : assetId,
      workspaceId: typeof parsed.workspaceId === 'string' ? parsed.workspaceId : '',
      title: typeof parsed.title === 'string' ? parsed.title : 'Untitled design',
      mode: parsed.mode === 'BOUND' || parsed.mode === 'FREE' ? parsed.mode : 'FREE',
      canvas: parsed.canvas ?? DEFAULT_DESIGN_CANVAS,
      shapes: Array.isArray(parsed.shapes) ? parsed.shapes : [],
      templateOf: typeof parsed.templateOf === 'string' ? parsed.templateOf : null,
      createdById: typeof parsed.createdById === 'string' ? parsed.createdById : '',
      createdByName: typeof parsed.createdByName === 'string' ? parsed.createdByName : 'Debug User',
      createdAt: null,
      updatedAt: null,
    }
  } catch {
    return null
  }
}

const listDebugDesignAssets = (workspaceId: string): DesignAsset[] => {
  try {
    return Object.keys(window.localStorage)
      .filter((key) => key.startsWith(DEBUG_DESIGN_PREFIX))
      .map((key) => readDebugDesignAsset(key.slice(DEBUG_DESIGN_PREFIX.length)))
      .filter((asset): asset is DesignAsset => asset !== null && asset.workspaceId === workspaceId)
  } catch {
    return []
  }
}

export type UseDesignAssetsOptions = {
  status?: 'COMPLETED' | string
  excludeTemplates?: boolean
}

const EMPTY_ASSETS: DesignAsset[] = []

export const useDesignAssets = (
  workspaceId: string | null,
  options?: UseDesignAssetsOptions,
) => {
  const statusFilter = options?.status
  const excludeTemplates = options?.excludeTemplates ?? (statusFilter === 'COMPLETED')
  const isPreview = isNoSignInPreviewActive()

  const [previewRevision, setPreviewRevision] = useState(0)

  useEffect(() => {
    if (!isPreview || !workspaceId) return
    const refresh = () => setPreviewRevision((prev) => prev + 1)
    window.addEventListener(DEBUG_DESIGN_SAVED_EVENT, refresh)
    return () => window.removeEventListener(DEBUG_DESIGN_SAVED_EVENT, refresh)
  }, [isPreview, workspaceId])

  const [subscribedState, setSubscribedState] = useState<{
    workspaceId: string
    items: DesignAsset[]
    error: Error | null
  } | null>(null)

  useEffect(() => {
    if (!workspaceId || isPreview) {
      return
    }
    const currentWorkspaceId = workspaceId
    const ref = query(
      collection(firestoreDb, DESIGN_ASSETS_COLLECTION),
      where('workspaceId', '==', currentWorkspaceId),
      orderBy('updatedAt', 'desc'),
    )
    return onSnapshot(
      ref,
      (snapshot) => {
        setSubscribedState({
          workspaceId: currentWorkspaceId,
          items: snapshot.docs.map(fromSnapshot),
          error: null,
        })
      },
      (error) => {
        setSubscribedState({
          workspaceId: currentWorkspaceId,
          items: EMPTY_ASSETS,
          error,
        })
      },
    )
  }, [workspaceId, isPreview])

  const rawItems = useMemo(() => {
    if (!workspaceId) return EMPTY_ASSETS
    if (isPreview) {
      void previewRevision
      return listDebugDesignAssets(workspaceId)
    }
    if (subscribedState?.workspaceId === workspaceId) {
      return subscribedState.items
    }
    return EMPTY_ASSETS
  }, [workspaceId, isPreview, previewRevision, subscribedState])

  const loading = Boolean(workspaceId && !isPreview && subscribedState?.workspaceId !== workspaceId)
  const error = (!workspaceId || isPreview || subscribedState?.workspaceId !== workspaceId)
    ? null
    : subscribedState.error

  const filteredItems = useMemo(() => {
    return rawItems.filter((asset) => {
      if (excludeTemplates && Boolean(asset.templateOf)) return false
      if (statusFilter === 'COMPLETED') {
        if (asset.boundOutputStatus === 'DRAFT') return false
      }
      return true
    })
  }, [rawItems, excludeTemplates, statusFilter])

  return {
    items: filteredItems,
    loading,
    error,
  }
}

export const useDesignAsset = (assetId: string | null) => {
  const isPreview = isNoSignInPreviewActive()
  const [previewRevision, setPreviewRevision] = useState(0)

  useEffect(() => {
    if (!isPreview || !assetId) return
    const refresh = () => setPreviewRevision((prev) => prev + 1)
    window.addEventListener(DEBUG_DESIGN_SAVED_EVENT, refresh)
    return () => window.removeEventListener(DEBUG_DESIGN_SAVED_EVENT, refresh)
  }, [isPreview, assetId])

  const [subscribedState, setSubscribedState] = useState<{
    assetId: string
    asset: DesignAsset | null
    error: Error | null
  } | null>(null)

  useEffect(() => {
    if (!assetId || isPreview) {
      return
    }
    const currentAssetId = assetId
    return onSnapshot(
      doc(firestoreDb, DESIGN_ASSETS_COLLECTION, currentAssetId),
      (snapshot) => {
        const asset = snapshot.exists()
          ? fromSnapshot(snapshot as QueryDocumentSnapshot<DocumentData>)
          : null
        setSubscribedState({ assetId: currentAssetId, asset, error: null })
      },
      (error) => {
        setSubscribedState({ assetId: currentAssetId, asset: null, error })
      },
    )
  }, [assetId, isPreview])

  const asset = useMemo(() => {
    if (!assetId) return null
    if (isPreview) {
      void previewRevision
      return readDebugDesignAsset(assetId)
    }
    if (subscribedState?.assetId === assetId) {
      return subscribedState.asset
    }
    return null
  }, [assetId, isPreview, previewRevision, subscribedState])

  const loading = Boolean(assetId && !isPreview && subscribedState?.assetId !== assetId)
  const error = (!assetId || isPreview || subscribedState?.assetId !== assetId)
    ? null
    : subscribedState.error

  return { asset, loading, error }
}
